import React, { useState, useEffect } from 'react';
import api from '../services/api';
import SearchableDropdown from './SearchableDropdown';
import '../App.css';

interface AddToTeamDialogProps {
  playerId: string;
  playerName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isGuest?: boolean;
  teamId?: string;
  matches?: Array<{ id: string; scheduledDate: string; status: string; homeTeam?: { name: string }; awayTeam?: { name: string } }>;
}

interface DrillDownTeam {
  id: string;
  name: string;
  clubId: string | null;
  clubName: string | null;
  leagueId: string | null;
  leagueName: string | null;
}

const AddToTeamDialog: React.FC<AddToTeamDialogProps> = ({
  playerId,
  playerName,
  isOpen,
  onClose,
  onSuccess,
  isGuest = false,
  teamId: presetTeamId,
  matches = []
}) => {
  const [drillDownTeams, setDrillDownTeams] = useState<DrillDownTeam[]>([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [selectedLeague, setSelectedLeague] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(presetTeamId || '');
  const [role, setRole] = useState('player');
  const [position, setPosition] = useState('');
  const [jerseyNumber, setJerseyNumber] = useState('');
  const [selectedMatch, setSelectedMatch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && !presetTeamId) {
      fetchDrillDown();
    }
  }, [isOpen, presetTeamId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedClub('');
      setSelectedLeague('');
      setSelectedTeam(presetTeamId || '');
      setRole('player');
      setPosition('');
      setJerseyNumber('');
      setSelectedMatch('');
      setError('');
    }
  }, [isOpen, presetTeamId]);

  const fetchDrillDown = async () => {
    setLoading(true);
    try {
      const response = await api.get('/teams/drill-down');
      setDrillDownTeams(response.data.teams || []);
    } catch (err) {
      console.error('Error fetching drill-down data:', err);
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  // Build club options from all teams
  const clubOptions = Array.from(new Map(
    drillDownTeams
      .filter(t => t.clubId && t.clubName)
      .map(t => [t.clubId as string, { id: t.clubId as string, label: t.clubName as string }])
  ).values());

  // Add option for teams without a club
  const teamsWithoutClub = drillDownTeams.filter(t => !t.clubId);
  if (teamsWithoutClub.length > 0) {
    clubOptions.push({ id: '__none__', label: '(No Club)' });
  }

  // League options filtered by selected club
  const leagueOptions = Array.from(new Map(
    drillDownTeams
      .filter(t => {
        if (!selectedClub) return false;
        if (selectedClub === '__none__') return !t.clubId;
        return t.clubId === selectedClub;
      })
      .filter(t => t.leagueId && t.leagueName)
      .map(t => [t.leagueId as string, { id: t.leagueId as string, label: t.leagueName as string }])
  ).values());

  // Add option for teams without a league
  const teamsWithoutLeague = drillDownTeams.filter(t => {
    if (!selectedClub) return false;
    const clubMatch = selectedClub === '__none__' ? !t.clubId : t.clubId === selectedClub;
    return clubMatch && !t.leagueId;
  });
  if (teamsWithoutLeague.length > 0) {
    leagueOptions.push({ id: '__none__', label: '(No League)' });
  }

  // Team options filtered by selected club + league
  const teamOptions = drillDownTeams
    .filter(t => {
      if (!selectedLeague) return false;
      const clubMatch = selectedClub === '__none__' ? !t.clubId : t.clubId === selectedClub;
      const leagueMatch = selectedLeague === '__none__' ? !t.leagueId : t.leagueId === selectedLeague;
      return clubMatch && leagueMatch;
    })
    .map(t => ({ id: t.id, label: t.name }));

  const handleClubChange = (value: string) => {
    setSelectedClub(value);
    setSelectedLeague('');
    setSelectedTeam('');
  };

  const handleLeagueChange = (value: string) => {
    setSelectedLeague(value);
    setSelectedTeam('');
  };

  const handleSubmit = async () => {
    const targetTeamId = presetTeamId || selectedTeam;
    if (!targetTeamId) {
      setError('Please select a team');
      return;
    }
    if (isGuest && !selectedMatch) {
      setError('Please select a match for the guest player');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (isGuest) {
        await api.post(`/teams/${targetTeamId}/members/guest`, {
          userId: playerId,
          matchId: selectedMatch,
          position: position || undefined,
          jerseyNumber: jerseyNumber ? parseInt(jerseyNumber) : undefined
        });
      } else {
        await api.post(`/teams/${targetTeamId}/members`, {
          userId: playerId,
          role,
          position: position || undefined,
          jerseyNumber: jerseyNumber ? parseInt(jerseyNumber) : undefined
        });
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add player to team');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const activeMatches = matches.filter(m => m.status === 'scheduled' || m.status === 'in_progress');

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '30px',
          maxWidth: '550px',
          width: '90%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>{isGuest ? 'Add Guest Player' : 'Add Player to Team'}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#757575',
              padding: '5px'
            }}
          >
            x
          </button>
        </div>

        <p style={{ color: '#757575', marginBottom: '20px' }}>
          Adding <strong>{playerName}</strong>
        </p>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '15px' }}>
            {error}
          </div>
        )}

        {/* Team Selection Drill-Down: Club > League > Team */}
        {!presetTeamId && (
          <>
            {loading ? (
              <p>Loading teams...</p>
            ) : (
              <>
                <SearchableDropdown
                  label="Club"
                  options={clubOptions}
                  value={selectedClub}
                  onChange={handleClubChange}
                  placeholder="Select a club..."
                />

                {selectedClub && (
                  <SearchableDropdown
                    label="League"
                    options={leagueOptions}
                    value={selectedLeague}
                    onChange={handleLeagueChange}
                    placeholder="Select a league..."
                  />
                )}

                {selectedLeague && (
                  <SearchableDropdown
                    label="Team"
                    options={teamOptions}
                    value={selectedTeam}
                    onChange={setSelectedTeam}
                    placeholder="Select a team..."
                  />
                )}
              </>
            )}
          </>
        )}

        {/* Role (not for guests) */}
        {!isGuest && (
          <div className="form-group">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="player">Player</option>
              <option value="captain">Captain</option>
              <option value="coach">Coach</option>
              <option value="manager">Manager</option>
            </select>
          </div>
        )}

        {/* Position */}
        <div className="form-group">
          <label>Position</label>
          <select value={position} onChange={(e) => setPosition(e.target.value)}>
            <option value="">Select Position (Optional)</option>
            <option value="Goalkeeper">Goalkeeper</option>
            <option value="Defender">Defender</option>
            <option value="Midfielder">Midfielder</option>
            <option value="Forward">Forward</option>
          </select>
        </div>

        {/* Jersey Number */}
        <div className="form-group">
          <label>Jersey Number</label>
          <input
            type="number"
            value={jerseyNumber}
            onChange={(e) => setJerseyNumber(e.target.value)}
            placeholder="Optional"
            min="1"
            max="99"
          />
        </div>

        {/* Match selector for guests */}
        {isGuest && (
          <div className="form-group">
            <label>Match *</label>
            <select value={selectedMatch} onChange={(e) => setSelectedMatch(e.target.value)} required>
              <option value="">Select a match</option>
              {activeMatches.map(match => (
                <option key={match.id} value={match.id}>
                  {new Date(match.scheduledDate).toLocaleDateString()} - {match.homeTeam?.name} vs {match.awayTeam?.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || (!presetTeamId && !selectedTeam)}
          >
            {submitting ? 'Adding...' : isGuest ? 'Add Guest Player' : 'Add to Team'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToTeamDialog;
